import React, { useEffect, useRef } from 'react';
import { Edit, RotateCcw } from 'lucide-react';
import { motion } from 'motion/react';
import { fadeInUp, staggerContainer } from '../constants/game';

interface ScorePanelProps {
  overs: string;
  innings: string;
  wicketsRemaining: number;
  runRate: string;
  projected: string;
  currentScore: string;
  battingTeam: string;
  isSecondInnings: boolean;
}

export function ScorePanel({
  overs,
  innings,
  wicketsRemaining,
  runRate,
  projected,
  currentScore,
  battingTeam,
  isSecondInnings,
}: ScorePanelProps) {
  return (
    <motion.div 
      className="bg-[#f2f2f7] relative rounded-lg shrink-0 w-full"
      variants={fadeInUp}
      whileHover={{ scale: 1.01 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <div className="relative size-full">
        <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start p-4 relative w-full">
          {/* Top row with overs, innings, wickets */}
          <div className="relative shrink-0 w-full">
            <div className="flex flex-row items-center justify-center relative size-full">
              <div className="box-border content-stretch flex flex-row gap-1 items-center justify-center px-1.5 py-0 relative w-full">
                <div className="basis-0 flex flex-row grow items-center self-stretch shrink-0">
                  <div className="basis-0 box-border content-stretch flex flex-col grow h-full items-start justify-center min-h-px min-w-px p-0 relative shrink-0 text-center">
                    <div className="relative shrink-0 text-[#34c759] text-[15px] tracking-[-0.5px]">
                      <p className="block leading-[20px]">
                        Overs
                      </p>
                    </div>
                    <div className="relative shrink-0 text-[#1c1c1e] text-[17px] tracking-[-0.408px]">
                      <p className="block leading-[22px]">
                        {overs}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row items-center self-stretch">
                  <div className="box-border content-stretch flex flex-col h-full items-center justify-center p-0 relative shrink-0 text-center">
                    <div className="relative shrink-0 text-[#34c759] text-[15px] tracking-[-0.5px]">
                      <p className="block leading-[20px]">
                        {innings}
                      </p>
                    </div>
                    <div className="relative shrink-0 text-[#1c1c1e] text-[17px] tracking-[-0.408px]">
                      <p className="block leading-[22px]">
                        Batting: {battingTeam}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="basis-0 flex flex-row grow items-center self-stretch shrink-0">
                  <div className="basis-0 box-border content-stretch flex flex-col grow h-full items-end justify-center min-h-px min-w-px p-0 relative shrink-0 text-right">
                    <div className="relative shrink-0 text-[#34c759] text-[15px] tracking-[-0.5px]">
                      <p className="block leading-[20px]">
                        Wickets
                      </p>
                    </div>
                    <div className="relative shrink-0 text-[#1c1c1e] text-[17px] tracking-[-0.408px]">
                      <p className="block leading-[22px]">
                        {wicketsRemaining}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Score section */}
          <motion.div 
            className="bg-white relative rounded-lg shrink-0 w-full"
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <div className="absolute border border-[rgba(120,120,128,0.16)] border-solid inset-0 pointer-events-none rounded-lg" />
            <div className="flex flex-row items-center justify-center relative size-full">
              <div className="box-border content-stretch flex flex-row gap-2.5 items-center justify-center p-4 relative w-full">
                <div className="box-border content-stretch flex flex-col items-start justify-center leading-[0] not-italic p-0 relative shrink-0 text-right w-20 sm:w-28">
                  <div className="font-semibold relative shrink-0 text-[#34c759] text-[13px] sm:text-[15px] tracking-[-0.5px]">
                    <p className="block leading-[20px]">
                      Run Rate
                    </p>
                  </div>
                  <div className="font-bold relative shrink-0 text-[#1c1c1e] text-[18px] sm:text-[22px] tracking-[0.35px]">
                    <p className="block leading-[28px]">
                      {runRate}
                    </p>
                  </div>
                </div>
                <div className="basis-0 box-border content-center flex grow items-center justify-center min-h-px min-w-px p-0 relative shrink-0">
                  <motion.div 
                    className="flex flex-col font-semibold justify-center leading-[0] not-italic relative shrink-0 text-[#1c1c1e] text-[32px] sm:text-[40px] text-center"
                    key={currentScore}
                    initial={{ scale: 1.1, opacity: 0.8 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  >
                    <p className="block leading-normal">
                      {currentScore}
                    </p>
                  </motion.div>
                </div>
                <div className="box-border content-stretch flex flex-col items-end justify-center leading-[0] not-italic p-0 relative shrink-0 text-right w-20 sm:w-28">
                  <div className="font-semibold relative shrink-0 text-[#34c759] text-[13px] sm:text-[15px] tracking-[-0.5px]">
                    <p className="block leading-[20px]">
                      {isSecondInnings ? 'Target' : 'Projected'}
                    </p>
                  </div>
                  <div className="font-bold relative shrink-0 text-[#1c1c1e] text-[18px] sm:text-[22px] tracking-[0.35px]">
                    <p className="block leading-[28px]">
                      {projected}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

interface PlayerPanelProps {
  name: string;
  score: number;
  balls: number;
  isOnStrike: boolean;
  onEdit: () => void;
  variant?: "normal" | "bowler";
  wickets?: number;
  isRetired?: boolean;
  isLastManStands?: boolean;
}

export function PlayerPanel({
  name,
  score,
  balls,
  isOnStrike,
  onEdit,
  variant = "normal",
  wickets,
  isRetired = false,
  isLastManStands = false,
}: PlayerPanelProps) {
  // Enhanced active state styling with proper blue background and border
  const bgColor =
    variant === "bowler"
      ? "bg-[#fff0f0]"
      : isLastManStands
        ? "bg-[#f3e8ff]"
        : isRetired
          ? "bg-[#fff7e0]"
          : isOnStrike
            ? "bg-[#deeeff]"
            : "bg-[#f2f2f7]";

  const textColor =
    variant === "bowler"
      ? "text-[#de2027]"
      : isLastManStands
        ? "text-[#7c3aed]"
        : isRetired
          ? "text-[#b45309]"
          : isOnStrike
            ? "text-[#0052a9]"
            : "text-[#545456]";

  const nameColor =
    variant === "bowler"
      ? "text-[#791b1e]"
      : isLastManStands
        ? "text-[#5b21b6]"
        : isRetired
          ? "text-[#92400e]"
          : isOnStrike
            ? "text-[#0052a9]"
            : "text-[#545456]";

  const iconColor =
    variant === "bowler" ? "text-[#de2027]" : "text-[#007aff]";

  // Show placeholder for Last Man Stands second batter
  const displayName = isLastManStands && name === "Last Man Stands" 
    ? "Last Man Stands" 
    : name + (isRetired ? ' (retired)' : '');

  const displayStats = isLastManStands && name === "Last Man Stands"
    ? "-"
    : variant === "bowler" 
      ? `${score}-${wickets} (${balls})` 
      : `${score} (${balls})`;

  return (
    <motion.div
      className={`${bgColor} relative rounded-lg shrink-0 w-full`}
      variants={fadeInUp}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      {/* Blue border for active batter */}
      {isOnStrike && variant !== "bowler" && !isRetired && !isLastManStands && (
        <motion.div 
          className="absolute border-2 border-[#007aff] border-solid inset-0 pointer-events-none rounded-lg"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        />
      )}
      {/* Orange border for retired batter */}
      {isRetired && (
        <motion.div 
          className="absolute border-2 border-[#f59e0b] border-solid inset-0 pointer-events-none rounded-lg"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        />
      )}
      {/* Purple border for Last Man Stands */}
      {isLastManStands && (
        <motion.div 
          className="absolute border-2 border-[#7c3aed] border-solid inset-0 pointer-events-none rounded-lg"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        />
      )}
      <div className="flex flex-row items-center relative size-full">
        <div className="box-border content-stretch flex flex-row gap-3 items-center justify-start p-3 relative w-full">
          <div className="basis-0 box-border content-stretch flex flex-row gap-2.5 grow items-center justify-start min-h-px min-w-px overflow-clip p-0 relative shrink-0">
            <div className="basis-0 box-border content-stretch flex flex-col gap-1 grow items-start justify-center min-h-px min-w-px p-0 relative shrink-0">
              <div className="box-border content-stretch flex flex-row gap-1 items-center justify-start p-0 relative shrink-0 w-full">
                {isOnStrike && variant !== "bowler" && !isRetired && !isLastManStands && (
                  <motion.div 
                    className="relative shrink-0 size-3"
                    initial={{ scale: 0, rotate: 180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
                  >
                    <svg
                      className="block size-full"
                      fill="none"
                      preserveAspectRatio="none"
                      viewBox="0 0 12 12"
                    >
                      <rect
                        fill="#DE2027"
                        fillOpacity="0.4"
                        height="12"
                        rx="6"
                        width="12"
                      />
                      <circle
                        cx="6"
                        cy="6"
                        fill="#DE2027"
                        r="3"
                      />
                    </svg>
                  </motion.div>
                )}
                <div
                  className={`font-semibold relative shrink-0 ${nameColor} text-[15px] text-left tracking-[-0.5px] truncate`}
                >
                  <p className="block leading-[20px] truncate">
                    {displayName}
                  </p>
                </div>
              </div>
              <div
                className={`font-semibold relative shrink-0 ${textColor} text-[13px] text-left tracking-[-0.078px] w-full`}
              >
                <motion.p 
                  className="block leading-[18px]"
                  key={`${score}-${balls}-${wickets}`}
                  initial={{ opacity: 0.7, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  {displayStats}
                </motion.p>
              </div>
            </div>
            {!(isLastManStands && name === "Last Man Stands") && (
              <motion.button
                onClick={onEdit}
                className={`${iconColor} text-[18px] text-center p-1 rounded hover:bg-white/20 transition-colors flex-shrink-0`}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Edit className="w-4 h-4" />
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

interface OverTrackerProps {
  currentOverBalls: (number | string)[];
  previousOvers: Array<(number | string)[]>;
  currentOverNumber: number;
  ballsPerOver: number;
  maxBallsPerOver?: number;
  isMaxBallsEnabled?: boolean;
}

export function OverTracker({ 
  currentOverBalls, 
  previousOvers,
  currentOverNumber,
  ballsPerOver,
  maxBallsPerOver,
  isMaxBallsEnabled 
}: OverTrackerProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentOverRef = useRef<HTMLDivElement>(null);
  
  const getBallColor = (ball: number | string) => {
    if (ball === 'W') return 'bg-[#ff556c]'; // Red for wicket
    if (ball === 6) return 'bg-[#cf66e7]'; // Purple for 6
    if (ball === 4) return 'bg-[#35a658]'; // Green for 4
    if (ball === 0) return 'bg-[#8e8e93]'; // Gray for dot ball
    if (ball === 'WD') return 'bg-[#ffa500]'; // Orange for wide
    if (ball === 'NB') return 'bg-[#ffa500]'; // Orange for no ball
    if (ball === 'B') return 'bg-[#8e8e93]'; // Gray for bye
    if (ball === 'LB') return 'bg-[#8e8e93]'; // Gray for leg bye
    return 'bg-[#8e8e93]'; // Gray for other runs
  };

  const getBallText = (ball: number | string) => {
    if (ball === 'WD') return 'W';
    if (ball === 'NB') return 'N';
    if (ball === 'B') return 'B';
    if (ball === 'LB') return 'L';
    return ball.toString();
  };

  const getBallTextColor = (ball: number | string) => {
    return 'text-white'; // All text is white
  };

  const getDisplayBalls = (balls: (number | string)[], isCurrentOver: boolean = false) => {
    if (isCurrentOver) {
      // Count legal balls (excluding WD and NB which are extras)
      const legalBallsBowled = balls.filter(ball => ball !== 'WD' && ball !== 'NB').length;
      
      // Calculate remaining legal balls that can be bowled
      const remainingLegalBalls = Math.max(0, ballsPerOver - legalBallsBowled);
      
      // Total indicators = actual balls bowled + remaining legal balls
      let totalIndicators = balls.length + remainingLegalBalls;
      
      // Cap at maxBallsPerOver if that setting is enabled
      if (isMaxBallsEnabled && maxBallsPerOver) {
        totalIndicators = Math.min(totalIndicators, maxBallsPerOver);
      }
      
      return totalIndicators;
    } else {
      // For previous overs: show actual number of balls that were bowled
      return Math.max(ballsPerOver, balls.length);
    }
  };

  const getBallCountText = (balls: (number | string)[]) => {
    if (isMaxBallsEnabled && maxBallsPerOver && maxBallsPerOver > ballsPerOver) {
      return `${balls.length} / ${maxBallsPerOver} balls max`;
    }
    return `${balls.length} ball${balls.length !== 1 ? 's' : ''}`;
  };

  // Auto-scroll to position current over at hard left with 16px padding
  useEffect(() => {
    if (scrollContainerRef.current && currentOverRef.current) {
      // Get the actual positions using DOM measurements
      const scrollContainer = scrollContainerRef.current;
      const currentOverElement = currentOverRef.current;
      
      // Get the bounding boxes
      const containerRect = scrollContainer.getBoundingClientRect();
      const currentOverRect = currentOverElement.getBoundingClientRect();
      
      // Calculate the current position of the current over relative to the container
      const currentOverLeftOffset = currentOverRect.left - containerRect.left + scrollContainer.scrollLeft;
      
      // Calculate the desired scroll position
      // We want the current over to be 16px from the left edge of the container
      const leftPadding = 16;
      const desiredScrollLeft = Math.max(0, currentOverLeftOffset - leftPadding);
      
      // Scroll to the calculated position
      scrollContainer.scrollTo({
        left: desiredScrollLeft,
        behavior: 'smooth'
      });
    }
  }, [currentOverNumber, previousOvers.length, currentOverBalls.length]);

  const renderOver = (overNumber: number, balls: (number | string)[], isCurrentOver: boolean = false) => {
    const displayBalls = getDisplayBalls(balls, isCurrentOver);
    
    return (
      <motion.div
        ref={isCurrentOver ? currentOverRef : undefined}
        key={overNumber}
        className={`flex-shrink-0 rounded-lg ${ 
          isCurrentOver ? 'bg-[#deeeff]' : 'bg-[#f2f2f7]'
        }`}
        // Removed fixed width constraints to allow hugging content
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <div className="flex flex-col gap-3 p-3">
          <div className="flex items-center justify-between">
            <div className={`font-semibold text-[15px] ${ 
              isCurrentOver ? 'text-[#0052a9]' : 'text-gray-500'
            }`}>
              Over {overNumber}
            </div>
            <div className="text-xs text-gray-600">
              {getBallCountText(balls)}
            </div>
          </div>
          
          {/* Flex layout to keep all balls in same row with 6px gaps */}
          <motion.div 
            className="flex gap-1.5 items-center" // Changed from grid to flex with gap-1.5 = 6px
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {[...Array(displayBalls)].map((_, i) => (
              <motion.div
                key={i}
                className={`flex items-center justify-center rounded-2xl size-8 flex-shrink-0 ${ // Added flex-shrink-0 to prevent shrinking
                  balls[i] !== undefined ? getBallColor(balls[i]) : 'bg-white'
                }`}
                variants={{
                  hidden: { scale: 0, opacity: 0 },
                  visible: { 
                    scale: 1, 
                    opacity: 1,
                    transition: {
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                      delay: i * 0.05
                    }
                  }
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {balls[i] !== undefined && (
                  <motion.span 
                    className={`text-xs font-semibold ${getBallTextColor(balls[i])}`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, delay: 0.1 }}
                  >
                    {getBallText(balls[i])}
                  </motion.span>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div 
      className="bg-[#f2f2f7] relative rounded-lg shrink-0 w-full"
      variants={fadeInUp}
    >
      <div className="overflow-hidden relative size-full">
        <div className="box-border content-stretch flex flex-col items-start justify-start p-3 relative w-full">
          <div 
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide w-full"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {/* Previous overs */}
            {previousOvers.map((balls, index) => (
              renderOver(index + 1, balls, false)
            ))}
            
            {/* Current over */}
            {renderOver(currentOverNumber, currentOverBalls, true)}
            
            {/* Next over preview (empty) */}
            {currentOverNumber < 20 && (
              <motion.div
                className="flex-shrink-0 bg-[#f8f9fa] rounded-lg opacity-50"
                // Removed fixed width constraints
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 0.5, x: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <div className="flex flex-col gap-3 p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-gray-500 text-[15px]">
                      Over {currentOverNumber + 1}
                    </div>
                    <div className="text-xs text-gray-400">
                      Next over
                    </div>
                  </div>
                  
                  <div className="flex gap-1.5 items-center"> {/* Changed from grid to flex with gap-1.5 = 6px */}
                    {[...Array(ballsPerOver)].map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-center rounded-2xl size-8 bg-white flex-shrink-0" // Added flex-shrink-0
                      >
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}